<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { FormQueryDocument, ParagraphForm, SubmitFormDocument } from '~/api/graphql/generated'
import { FormKitMessages } from '@formkit/vue'
import { useRecaptcha } from '~/api/drupal/useRecaptcha'
import type { RawDateString } from '~/types/shopware'
import { Option } from '~/components/atoms/forms/select/Select.vue'
import { ConfigSettingsDocument } from '~/api/graphql/generated'
import { useI18n } from 'vue-i18n'

export interface ReturnElementProps {
  is: string
  id?: string
  name: string
  label: string
  required: boolean
  validation: string
  validationMessages: {
    [key: string]: string
  }
  help?: string
  defaultValue?: string
  options?: {
    label: string
    value: string
  }[]
  after?: RawDateString
  before?: RawDateString
  step?: number
  rows?: number
  html?: string
  htmlLabel?: boolean
}

const props = defineProps<{
  entity: ParagraphForm
}>()

const localePath = useLocalePath()
const { t } = useI18n()
const { generateToken, verifyToken } = useRecaptcha()
const gtm = useGtm()

const { data: formData } = await useAsyncQuery(FormQueryDocument, {
  webformId: props.entity?.fieldForm?.entity?.entityId,
})

const { data: configToccoSettings } = await useAsyncQuery(ConfigSettingsDocument)

const pages = computed(() => {
  return {
    privacyPolicy: localePath(configToccoSettings.value?.configCareumLegalSettings?.privacyPolicyUrl ?? '/'),
  }
})

const legal = computed(() => {
  return {
    privacyPolicy: {
      label: t('form.privacyPolicy.text', {
        hyperlink: `<a href="${pages.value.privacyPolicy}" target="_blank">
        ${t('form.privacyPolicy.hyperlink')}
      </a>`,
      }),
      error: t('form.privacyPolicy.error'),
    },
  }
})

const form = ref(formData.value?.webformById)
const formErrors = ref()
const inputErrors: Ref<Record<string, string[]>> = ref({})

const elements = form.value?.elements
  ?.filter((element: any) => element.type !== 'managed_file' && element.type !== 'webform_actions')
  .map((element: any) => {
    const returnElement: ReturnElementProps = {
      is: '',
      id: `${props.entity?.fieldForm?.entity?.entityId}-${element.id}`,
      name: element.id,
      label: element.title,
      htmlLabel: false,
      help: element.description ?? '',
      required: false,
      validation: '',
      validationMessages: {},
    }

    // general options
    if (element.multiple) {
      returnElement.defaultValue = element.defaultValue
    } else {
      returnElement.defaultValue = element.defaultValue ? element.defaultValue[0] : null
    }

    if (element.step) {
      returnElement.step = element.step
    }

    if (element.options) {
      returnElement.options = element.options.map(
        (option: any): Option => ({
          value: option.value,
          label: option.title,
        }),
      )
    }

    if (element.emptyOption && element.emptyOption.title) {
      returnElement.options?.unshift({
        value: element.emptyOption.value,
        label: element.emptyOption.title,
      })
    }

    // Validation
    const arrayValidations: string[] = []

    if (element.required) {
      returnElement.required = true
      if (element.required.message) {
        returnElement.validationMessages.required = element.required.message
        returnElement.validationMessages.accepted = element.required.message
      }
    }

    if (element.maxLength) {
      arrayValidations.push(`length:${element.minLength ?? 0},${element.maxLength}`)
    } else {
      if (element.minLength) {
        arrayValidations.push(`length:${element.minLength}`)
      }
    }

    switch (element.type) {
      case 'textfield': {
        returnElement.is = 'Input'
        break
      }
      case 'number': {
        returnElement.is = 'Number'
        if (element.min) {
          arrayValidations.push(`min:${element.min}`)
        }
        if (element.max) {
          arrayValidations.push(`max:${element.max}`)
        }
        break
      }
      case 'select': {
        returnElement.is = 'Select'
        break
      }
      case 'email':
        returnElement.is = 'Email'
        break
      case 'date':
        returnElement.is = 'Date'
        if (element.dateMin) {
          returnElement.after = element.dateMin
        }
        if (element.dateMax) {
          returnElement.before = element.dateMax
        }
        break
      case 'hidden':
        returnElement.is = 'Hidden'
        break
      case 'checkbox':
        returnElement.is = 'Checkbox'
        // Verify if the element.title contains HTML, if so, set htmlLabel to true.
        if (element.title.match(/<[^>]*>/)) {
          returnElement.htmlLabel = true
        }
        break
      case 'toggle_switch':
        returnElement.is = 'Toggle'
        break
      case 'checkboxes':
        returnElement.is = 'Checkbox'
        if (element.multiple?.limit) {
          arrayValidations.push(`max:${element.multiple.limit}`)

          if (element.multiple.message) {
            returnElement.validationMessages.max = element.multiple.limit
          }
        }
        break
      case 'radios':
        returnElement.is = 'Radio'
        break
      case 'tel':
        returnElement.is = 'TelInput'
        break
      case 'textarea':
        returnElement.is = 'Textarea'
        if (element.rows) {
          returnElement.rows = element.rows
        }
        break
      case 'webform_markup':
        returnElement.is = 'WebformMarkup'
        returnElement.html = element.markup
        break
    }

    //prepare validation
    returnElement.validation = arrayValidations.join('|')

    return returnElement
  })

const submitElements = form.value?.elements
  ?.filter((element: any) => element.type === 'webform_actions')
  .map((element: any) => {
    const returnElement = {
      is: 'Button',
      name: element.id,
      label: element.submitLabel,
    }
    return returnElement
  })

const showForm = ref(
  form.value?.settings?.limitTotal && parseInt(form.value?.submissions_count ?? '') >= form.value?.settings?.limitTotal,
)

const { mutate } = useMutation(SubmitFormDocument)

//TODO: add types
const handleSubmit = async (formData: any, node: any) => {
  formErrors.value = []
  inputErrors.value = {}

  const token = await generateToken('sectionFormRecaptcha')
  if (!token) {
    formErrors.value = [t('form.errors.generalError')]
    return
  }

  const recaptchaResult = await verifyToken(token)
  if (!recaptchaResult) {
    formErrors.value = [t('form.errors.generalError')]

    return
  }

  formData = {
    webform_id: props.entity.fieldForm?.entity?.entityId,
    ...formData,
  }

  const result = await mutate({ values: JSON.stringify(formData) })

  gtm?.trackEvent({
    webform_submission: props.entity.fieldForm?.entity?.entityLabel,
  })

  if (result && result.data?.submitForm) {
    const data = result.data

    if (data.submitForm?.errors) {
      data.submitForm.errors.forEach((error) => {
        if (!error?.key) return

        inputErrors.value[error.key] = [error?.message?.replace(/(<([^>]+)>)/gi, '') ?? '']
      })
    }

    if (data.submitForm?.submission && data.submitForm.submission.id && form.value?.confirmation_type === 'url') {
      navigateTo({ path: form.value?.confirmation_url ?? '/' })
    }
  } else {
    formErrors.value = [t('form.errors.generalError')]
  }

  return
}
</script>

<template>
  <Section>
    
<FormKitLazyProvider config-file="true">
<div class="t-10 t-o-1 tl-7 tl-o-0 ds-7 ds-o-1">
      <div v-if="showForm" v-html="form?.settings?.limitTotalMessage || t('form.defaultFormLimitMessage')" />
      <FormKit type="form" :actions="false" :input-errors="inputErrors" :errors="formErrors" @submit="handleSubmit">
        <div v-animate="{ animation: 'vertical-reveal' }" class="form-section--2-cols">
          <component :is="element.is" v-for="element in elements" :key="element.name" v-bind="element" />

          <Checkbox
            id="section-form-privacy-policy"
            name="privacyPolicy"
            :label="legal.privacyPolicy.label"
            :html-label="true"
            :required="true"
            :validation-messages="{ accepted: legal.privacyPolicy.error }"
          />
        </div>

        <RecaptchaInfo />

        <div v-animate="{ animation: 'vertical-reveal' }" class="buttons-wrapper">
          <FormKitMessages />
          <component :is="element.is" v-for="element in submitElements" :key="element.name" primary slide>
            {{ element.label }}
          </component>
        </div>
      </FormKit>
    </div>
</FormKitLazyProvider>

  </Section>
</template>

<style lang="scss" scoped>
.form-section--2-cols {
  grid-template-columns: 1fr;

  @include breakpoint(t) {
    grid-template-columns: 1fr 1fr;
  }

  :deep(.formkit-outer) {
    &[data-family='box'],
    &[data-type='textarea'] {
      @include breakpoint(t) {
        grid-column: span 2;
      }
    }
  }
}

.buttons-wrapper {
  margin-top: 72px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 30px;

  @include breakpoint(tl) {
    margin-top: 80px;

    align-items: center;
    justify-content: flex-end;
    flex-direction: row;
  }

  .formkit-messages {
    margin-right: auto;

    display: flex;
    justify-content: center;
  }
}
</style>
